import { useCallback, useRef, useState } from 'react';
import { useRenderInvoice, type ReadyRenderResponse } from '@repo/queries';

export interface UseInvoicePdfResult {
  downloading: boolean;
  sharing: boolean;
  error: string | null;
  downloadPdf: () => Promise<void>;
  sharePdf: () => Promise<void>;
}

/**
 * Render an invoice PDF via the printer pipeline, then download or
 * share through browser APIs.
 */
export function useInvoicePdf(orgId: string, invoiceId: string, filename: string): UseInvoicePdfResult {
  const { renderAndWait, loading } = useRenderInvoice(orgId, invoiceId);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchBlob = useCallback(
    async (ready: ReadyRenderResponse): Promise<Blob> => {
      const res = await fetch(ready.pdfUrl, { signal: abortRef.current?.signal });
      if (!res.ok) {
        throw new Error(`Could not download PDF (${res.status})`);
      }
      return res.blob();
    },
    [],
  );

  const triggerDownload = useCallback((blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name.endsWith('.pdf') ? name : `${name}.pdf`;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, []);

  const runRender = useCallback(async () => {
    setError(null);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    return renderAndWait({}, { signal: abortRef.current.signal });
  }, [renderAndWait]);

  const downloadPdf = useCallback(async () => {
    try {
      const ready = await runRender();
      const blob = await fetchBlob(ready);
      triggerDownload(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
      throw err;
    }
  }, [runRender, fetchBlob, triggerDownload, filename]);

  const sharePdf = useCallback(async () => {
    setSharing(true);
    try {
      const ready = await runRender();
      const blob = await fetchBlob(ready);
      const file = new File([blob], filename.endsWith('.pdf') ? filename : `${filename}.pdf`, {
        type: 'application/pdf',
      });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: filename,
        });
      } else {
        triggerDownload(blob, filename);
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Share failed');
      }
      throw err;
    } finally {
      setSharing(false);
    }
  }, [runRender, fetchBlob, triggerDownload, filename]);

  return {
    downloading: loading,
    sharing,
    error,
    downloadPdf,
    sharePdf,
  };
}
